import type { DragEvent } from "react";
import { useCallback, useRef, useState } from "react";

// A drag carries "Files" in its types from dragenter on, even though the files themselves are only
// readable at drop time. Text and link drags are left to the browser.
function carriesFiles(event: DragEvent<HTMLElement>): boolean {
  return Array.from(event.dataTransfer.types).includes("Files");
}

export type FileDropProps = {
  onDragEnter: (event: DragEvent<HTMLElement>) => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragLeave: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
};

/**
 * Makes a container a drop target for files dragged in from the desktop. Spread `dropProps` onto
 * it; `isDragging` is true while a file drag hovers it or any child. A drop is always swallowed so
 * the browser doesn't open the file; while `disabled` it is swallowed without calling `onDrop`.
 */
export function useFileDrop({ onDrop, disabled = false }: { onDrop: (files: File[]) => void; disabled?: boolean }): {
  isDragging: boolean;
  dropProps: FileDropProps;
} {
  const [isDragging, setIsDragging] = useState(false);
  // dragenter/dragleave fire for every child the cursor crosses, so only the balance of the two
  // tells a leave of the container from a leave of one of its children.
  const depthRef = useRef(0);

  const handleDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    if (!carriesFiles(event)) {
      return;
    }
    event.preventDefault();
    depthRef.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!carriesFiles(event)) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = disabled ? "none" : "copy";
    },
    [disabled]
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!carriesFiles(event)) {
      return;
    }
    depthRef.current = Math.max(0, depthRef.current - 1);
    if (depthRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!carriesFiles(event)) {
        return;
      }
      event.preventDefault();
      depthRef.current = 0;
      setIsDragging(false);
      if (disabled) {
        return;
      }
      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) {
        onDrop(files);
      }
    },
    [disabled, onDrop]
  );

  return {
    isDragging,
    dropProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop
    }
  };
}
